import { decode } from 'html-entities';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import {
	FolderExplorerNodeActionState,
	FolderExplorerNodeType,
	FolderExplorerTreeNode,
	FolderExplorerTreeNodeData,
	NotebookTreeNode,
	OrganizationTreeNode,
	SharedQueryShareUser,
	SharedQueryTreeNode,
	SharedQueryVirtualGroup,
} from 'src/models/organization';

import { decodeBase64 } from 'src/util/common';

const ROOT_NOTEBOOK_ID = 'tree_notebooks';
const ROOT_SHARED_QUERY_ID = 'tree_sharedQueries';

const normalizeNullableString = (value: null | string | undefined): null | string => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	return value;
};

const parseBooleanLike = (value: boolean | null | string | undefined): boolean => {
	if (typeof value === 'boolean') {
		return value;
	}

	if (value === null || value === undefined || value === '') {
		return false;
	}

	return /^(true|1)$/i.test(value.trim());
};

const parseNumberLike = (value: null | number | string | undefined): null | number => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (value === null || value === undefined || value === '') {
		return null;
	}

	const parsedValue = Number(value);

	return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const decodeBase64JsonArray = <T>(value: null | string | undefined): T[] => {
	if (value === null || value === undefined || value === '') {
		return [];
	}

	try {
		const parsed: unknown = JSON.parse(decodeBase64(value));
		return Array.isArray(parsed) ? (parsed as T[]) : [];
	} catch {
		return [];
	}
};

type SharedQueryRouteName =
	| 'agendaList'
	| 'documentList'
	| 'filterList'
	| 'mailList'
	| 'messageList'
	| 'noteList'
	| 'phoneCallList'
	| 'records'
	| 'taskList'
	| 'trashList';

interface SharedQueryNavigationTarget {
	instanceId: null | string;
	routeName: SharedQueryRouteName | null;
	supported: boolean;
}

const normalizeLabelAndCount = (
	rawName: string,
	explicitCount?: null | number | string
): { activityCount: number; label: string } => {
	const decodedName = decode(rawName);
	const normalizedExplicitCount = parseNumberLike(explicitCount);

	if (normalizedExplicitCount !== null) {
		return {
			activityCount: normalizedExplicitCount,
			label: decodedName.replace(/\s\([0-9]+\)$/, ''),
		};
	}

	const countMatch = decodedName.match(/\s\(([0-9]+)\)$/);
	if (!countMatch) {
		return { activityCount: 0, label: decodedName };
	}

	return {
		activityCount: Number(countMatch[1]),
		label: decodedName.replace(/\s\([0-9]+\)$/, ''),
	};
};

const resolveActionState = (node: OrganizationTreeNode): FolderExplorerNodeActionState => {
	if (node.type === 'sharedQuery') {
		return node.desktopApplicationItemId || node.idoInstanceId ? 'editable' : 'unsupported';
	}

	return 'editable';
};

const buildNodeData = (
	treeName: FolderExplorerTreeNodeData['treeName'],
	nodeType: FolderExplorerNodeType,
	node: OrganizationTreeNode
): FolderExplorerTreeNodeData => {
	const { activityCount } = normalizeLabelAndCount(node.name, node.activityCount ?? null);

	return {
		actionState: resolveActionState(node),
		activityCount,
		createdByTheAuthenticatedUser: parseBooleanLike(node.createdByTheAuthenticatedUser),
		creatorInitial: normalizeNullableString(node.creatorInitial ?? null),
		creatorUserId: parseNumberLike(node.creatorUserId),
		desktopApplicationItemId: normalizeNullableString(node.desktopApplicationItemId ?? null),
		identifier: normalizeNullableString(node.identifier ?? null),
		idoIdoListToolbarCalculationIsActive: parseBooleanLike(node.idoIdoListToolbarCalculationIsActive),
		idoInstanceId: normalizeNullableString(node.idoInstanceId ?? null),
		modelType: normalizeNullableString(node.modelType ?? null),
		rawName: node.name,
		serializedDesktopModuleFilterMemento: normalizeNullableString(
			node.serializedDesktopModuleFilterMemento ?? null
		),
		serializedIdoIdoListToolbarHiddenDetailsSearchString: normalizeNullableString(
			node.serializedIdoIdoListToolbarHiddenDetailsSearchString ?? null
		),
		shareableUsers: decodeBase64JsonArray<SharedQueryShareUser>(node.serializedUsersToShareQueryWith),
		shareableVirtualGroups: decodeBase64JsonArray<SharedQueryVirtualGroup>(node.serializedVirtualGroups),
		sharedWithUsers: decodeBase64JsonArray<SharedQueryShareUser>(node.serializedUsersAlreadySharedWith),
		treeName,
		type: nodeType,
	};
};

const resolvePrimeVueNodeIcon = (nodeType: FolderExplorerNodeType): Pick<FolderExplorerTreeNode, 'icon'> => {
	const iconPrefix = useUserInterfaceSettingsStore().menuItemsSolid ? 'fa-solid' : 'fa-regular';
	switch (nodeType) {
		case 'notebook':
		case 'sharedQueriesContainer':
			return {
				icon: `${iconPrefix} fa-folder`,
			};
		case 'sharedQuery':
			return {
				icon: `${iconPrefix} fa-list`,
			};
	}
};

const createPrimeTreeNode = (
	treeName: FolderExplorerTreeNodeData['treeName'],
	nodeType: FolderExplorerNodeType,
	node: OrganizationTreeNode
): FolderExplorerTreeNode => {
	const { label } = normalizeLabelAndCount(node.name, node.activityCount ?? null);
	const actionState = resolveActionState(node);
	const hasLazyChildrenHint = node.leaf !== undefined && !parseBooleanLike(node.leaf);
	const children = Array.isArray(node.children)
		? treeName === 'notebooks'
			? mapNotebookTreeNodes(node.children as NotebookTreeNode[])
			: mapSharedQueryTreeNodes(node.children as SharedQueryTreeNode[])
		: [];

	return {
		children,
		...resolvePrimeVueNodeIcon(nodeType),
		data: {
			...buildNodeData(treeName, nodeType, node),
			actionState,
		},
		draggable: node.id !== ROOT_NOTEBOOK_ID && node.id !== ROOT_SHARED_QUERY_ID,
		droppable: nodeType !== 'sharedQuery',
		key: node.id,
		label,
		leaf: children.length === 0 ? !hasLazyChildrenHint : false,
		selectable: node.id !== ROOT_NOTEBOOK_ID && node.id !== ROOT_SHARED_QUERY_ID && actionState !== 'unsupported',
		type: nodeType,
	};
};

export const mapNotebookTreeNodes = (nodes: NotebookTreeNode[]): FolderExplorerTreeNode[] => {
	return nodes.map((node) => createPrimeTreeNode('notebooks', 'notebook', node));
};

export const mapSharedQueryTreeNodes = (nodes: SharedQueryTreeNode[]): FolderExplorerTreeNode[] => {
	return nodes.map((node) =>
		createPrimeTreeNode(
			'sharedQueries',
			node.type === 'sharedQuery' ? 'sharedQuery' : 'sharedQueriesContainer',
			node
		)
	);
};

export const flattenTreeNodeMap = (nodes: OrganizationTreeNode[]): Map<string, OrganizationTreeNode> => {
	const entries = new Map<string, OrganizationTreeNode>();

	const visit = (nodeList: OrganizationTreeNode[]) => {
		for (const node of nodeList) {
			entries.set(node.id, node);
			if (Array.isArray(node.children) && node.children.length > 0) {
				visit(node.children as OrganizationTreeNode[]);
			}
		}
	};

	visit(nodes);
	return entries;
};

export const resolveSharedQueryNavigationTarget = (node: FolderExplorerTreeNode): SharedQueryNavigationTarget => {
	const desktopApplicationItemId = node.data.desktopApplicationItemId?.toLowerCase() ?? '';
	if (node.data.idoInstanceId) {
		return {
			instanceId: node.data.idoInstanceId,
			routeName: 'records',
			supported: true,
		};
	}

	const mappings: Array<[SharedQueryRouteName, RegExp]> = [
		['mailList', /mail/],
		['messageList', /message/],
		['taskList', /task/],
		['documentList', /document/],
		['phoneCallList', /phone.?call|call/],
		['noteList', /note|memo/],
		['trashList', /trash/],
		['filterList', /filter/],
		['agendaList', /appointment|calendar|agenda/],
	];

	for (const [routeName, pattern] of mappings) {
		if (pattern.test(desktopApplicationItemId)) {
			return {
				instanceId: null,
				routeName,
				supported: true,
			};
		}
	}

	return {
		instanceId: null,
		routeName: null,
		supported: false,
	};
};
